/**
 * Merkezi Public GORUNTULEME tracking
 * ====================================
 * Self-track ETMEYEN tüm public sayfalar (anasayfa, SEO landing, hub index,
 * blog, yasal) için tek noktadan GORUNTULEME → PublicEtkilesim. _app.js route
 * değişiminde çağrılır. Böylece "kullanıcı hangi sayfaya indi" attribution'ı
 * TÜM public yüzeyde çalışır (eskiden sadece örnek-detay + araç track ediyordu).
 *
 * Self-track eden sayfalar (useOrnekTracking/useToolTracking/usePublicTracking
 * ile semantic slug + indirme/CTA) HARİÇ tutulur → çift-track olmaz.
 *
 * isPublicRoute (routePolicy SSoT) + trackPublicEvent (publicTrackingApi SSoT)
 * yeniden kullanılır — yeni katman/duplike değil, kapsama tamamlama.
 *
 * NOT: Tüm eşleştirme `asPath` (gerçek URL) üzerinden — `router.pathname` (route
 * pattern) KULLANILMAZ; _app effect'i mount'ta router'ı kapattığı için pathname
 * stale olabilir (Next pages router). asPath, routeChangeComplete callback arg'ı
 * olarak HER ZAMAN güncel gelir → stale-closure yok.
 */

import { isPublicRoute } from '@/application/auth/routePolicy';
import { trackPublicEvent } from '@/application/services/publicTrackingApi';
import { logErrorToBackend } from '@/shared/utils/errorLogger';

// Araç alt sayfaları ToolPageLayout ile self-track (useToolTracking).
const TOOL_PREFIX = '/araclar/';

// Örnek DETAY sayfaları (modul/ornekler/<slug>) useOrnekTracking ile self-track.
// HUB index (modul/ornekler — slug yok) self-track ETMEZ → merkezi kapsar.
const ORNEK_DETAY_RE =
  /^\/(risk-analizi|saha-gozlem|egitim-sunum|acil-durum-plani|tatbikat-plani|yillik-calisma-plani|yillik-egitim-plani)\/ornekler\/[^/]+/;

// Son atılan slug — ardışık çift-GORUNTULEME guard (StrictMode double-mount +
// _app yeniden-render). Tek SPA oturumu için module-level yeterli (usePublicTracking
// ref pattern'inin SPA-ölçekli karşılığı). Component ref'e gerek yok → _app sade.
let _lastFiredSlug: string | null = null;

/** Query/hash'i atıp baştaki/sondaki slash'leri normalize eder. "/" korunur. */
function cleanPath(asPath: string): string {
  const p = (asPath || '/').split('?')[0].split('#')[0];
  if (p === '/' || p === '') return '/';
  return p.replace(/\/+$/, ''); // sondaki slash temizle, baştakini koru
}

/** Sayfa kendi tracking hook'unu çağırıyor mu (merkezi atlamalı)? */
function isSelfTracked(cleanedPath: string): boolean {
  if (cleanedPath === '/ornekler') return true; // master hub — usePublicTracking('ornekler')
  if (cleanedPath.startsWith(TOOL_PREFIX)) return true; // araç alt sayfaları
  if (ORNEK_DETAY_RE.test(cleanedPath)) return true; // örnek detay (slug'lı)
  return false;
}

/**
 * URL → kebab slug (PublicEtkilesim.slug, SLUG_REGEX [a-z0-9-]{2,80}).
 * "/" → "anasayfa", "/isg-yazilimi" → "isg-yazilimi", "/blog/x" → "blog-x".
 */
export function centralPublicSlug(asPath: string): string {
  const clean = cleanPath(asPath).replace(/^\/+/, '');
  if (!clean) return 'anasayfa';
  return clean
    .replace(/\//g, '-')
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .slice(0, 80);
}

/**
 * Merkezi public GORUNTULEME tetikle (fire-and-forget). Gate dışıysa no-op.
 * @param asPath router.asPath (gerçek URL — routeChangeComplete callback arg'ı)
 */
export function trackCentralPublicPageView(asPath: string): void {
  // Root _app effect'inden çağrılır → tracking ASLA sayfayı bozmamalı (defense-in-depth).
  try {
    const path = cleanPath(asPath);
    if (!isPublicRoute(path)) return; // public değil (app/auth/bootstrapping) → atla
    if (isSelfTracked(path)) return; // sayfa kendi track ediyor → atla
    const slug = centralPublicSlug(asPath);
    if (slug.length < 2) return; // SLUG_REGEX min 2
    if (_lastFiredSlug === slug) return; // ardışık çift-GORUNTULEME guard
    _lastFiredSlug = slug;
    trackPublicEvent(slug, 'GORUNTULEME');
  } catch (err) {
    // Render'ı ASLA bozma, ama SESSİZ YUTMA — beklenmedik kod hatası ErrorLog'a düşsün
    // (Sentry-grade görünürlük, CLAUDE.md 5.5). Beklenen fetch-fail (ad-blocker/ağ)
    // trackPublicEvent içinde zaten yutulur; burası SADECE kod buglarını yakalar.
    logErrorToBackend({
      errorType: 'PublicPageViewTrackingError',
      errorMessage: err instanceof Error ? err.message : String(err),
      errorStack: err instanceof Error ? err.stack : undefined,
      severity: 'WARNING',
      metadata: { asPath, lastFiredSlug: _lastFiredSlug },
    }).catch(() => { /* logger'ın kendi hatası da render'ı bozmasın */ });
  }
}
